import { AsyncPipe, Location } from "@angular/common";
import { Component, ChangeDetectionStrategy, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ReactiveFormsModule } from "@angular/forms";
import { Router, RouterLinkActive } from '@angular/router';
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { AccordionModule } from "core/components/accordion/accordion.module";
import { BadgeComponent } from "core/components/badge/badge.component";
import { ButtonModule } from "core/components/button/button.module";
import { EnhancedFormControl } from "core/components/form-extensions/generic-form";
import { IconsModule } from "core/components/icons/icons.module";
import { SelectInputComponent } from "core/components/input/select-input/select-input.component";
import { MediaClassDirective } from "core/directives/media-class.directive";
import { MediaIfDirective } from "core/directives/media-if.directive";
import { ModalService } from "core/modal/services/modal.service";
import { UIItem } from "core/models/ui-item";
import { SafeHtmlPipe } from "core/pipes/safe-html.pipe";
import { navigateByUrl } from "core/utils/router";
import { iconAngleDownLg } from "main/icons/icon-angle-down-lg";
import { iconCertificateMedal } from "main/icons/icon-certificate-medal";
import { iconFacebook } from "main/icons/icon-facebook";
import { iconFireworksStar } from "main/icons/icon-fireworks-star";
import { iconInsta } from "main/icons/icon-insta";
import { iconLanguage } from "main/icons/icon-language";
import { iconLinkedin } from "main/icons/icon-linkedin";
import { iconLogIn } from "main/icons/icon-log-in";
import { iconLogo } from "main/icons/icon-logo";
import { iconMail } from "main/icons/icon-mail";
import { iconServer } from "main/icons/icon-server";
import { iconShield } from "main/icons/icon-shield";
import { iconSupport } from "main/icons/icon-support";
import { iconX } from "main/icons/icon-x";
import { debounceTime } from "rxjs";
import { AppService } from "src/app/app.service";
import { clearSegmentsFromUrl, translateSegmentsFromEnglish, translateSegmentsToEnglish } from "src/app/i18n.serializer";
import { AllowedLangsEnum, AllowedLocalesEnum } from "src/app/locale/locale.enum";
import { LocaleService } from "src/app/locale/locale.service";
import { RoutesEnum } from "src/app/locale/routes.enum";
import { AvailableCountryISOEnum } from "src/app/models/language.enum";
import { TouchRouterLinkDirective } from 'core/directives/touch-router-link.directive';
import { TouchHoverDirective } from "core/directives/touch-hover.directive";

@Component({
  selector: 'app-footer',
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss'],
  standalone: true,
  host: { ngSkipHydration: 'true' },
  imports: [BadgeComponent, ButtonModule, TouchHoverDirective, TranslateModule, SafeHtmlPipe, RouterLinkActive, TouchRouterLinkDirective, IconsModule, AccordionModule, MediaIfDirective, MediaClassDirective, ReactiveFormsModule, SelectInputComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FooterComponent implements OnInit {
  readonly supportIcon = iconSupport;
  readonly mailIcon = iconMail;
  readonly loginIcon = iconLogIn;
  readonly angleDownIcon = iconAngleDownLg;
  readonly logoIcon = iconLogo;
  readonly xIcon = iconX;
  readonly fbIcon = iconFacebook;
  readonly instagramIcon = iconInsta;
  readonly langIcon = iconLanguage;
  readonly linkedInIcon = iconLinkedin;
  readonly certIcon = iconCertificateMedal;
  readonly serverIcon = iconServer;
  readonly shieldIcon = iconShield;
  readonly starIcon = iconFireworksStar;

  emojis = ['⚡️', '❤️', '🔥'];
  emoji = this.emojis[Math.floor(Math.random() * this.emojis.length)];
  
  regionItems = [
    new UIItem('Österreich', {code: 'de-AT'}, undefined, 'at'), 
    new UIItem('Deutschland', {code: 'de'}, undefined, 'de'),
    new UIItem('Global', {code: 'glob'}, undefined, 'glob')
  ];
  languageItems = [
    new UIItem('English', {code: 'en'}, undefined), 
    new UIItem('Deutsch', {code: 'de'}, undefined)
  ];
  
  routesEnum = RoutesEnum;
  regionControl = new EnhancedFormControl(this.regionItems[0]);
  languageControl = new EnhancedFormControl(this.languageItems[0]);

  constructor(public app: AppService, translateService: TranslateService, location: Location, public localeService: LocaleService, private _modal: ModalService) { 
    this.regionControl.valueChanges.pipe(debounceTime(300), takeUntilDestroyed()).subscribe((e) => {
      this.changeRegion(e.isoCode);
    });
    this.languageControl.setValue(this.languageItems.filter(l => l.data.code === app.currentLangIso)[0] || this.languageItems[0]);
    this.languageControl.valueChanges.pipe(debounceTime(300), takeUntilDestroyed()).subscribe((e) => {
      translateService.use(e.data.code).subscribe(() => {
          const newLang = e.data.code as AllowedLangsEnum;
          const oldLang = app.currentLangIso as AllowedLangsEnum;
          // Get current route (without /at/de)
          const currentUrl = clearSegmentsFromUrl(location.path(false));
          // convert to internal English version (based on old language)
          const englishUrl = translateSegmentsToEnglish(currentUrl, oldLang);
          // translate that English URL to new language
          const localizedUrl = translateSegmentsFromEnglish(englishUrl, newLang);
          app.currentLangIso = newLang;
          (window as any).cookiehub.setLanguage(newLang);
          navigateByUrl(localizedUrl || '/', { replaceUrl: true }, true).then(() => {
            this.app.getPromocodes().subscribe();
          });
      });
    });
  }

  ngOnInit(): void {
    this.localeService.appLocale$.subscribe(prefix => {
      if (prefix) {
        // @ts-ignore
        this.setRegion(AvailableCountryISOEnum[prefix.toUpperCase()])
      } else {
        this.setRegion(AvailableCountryISOEnum.OTHER)
      }
    });
  }

  private setRegion(isoCode: AvailableCountryISOEnum): void {
    const newRegion = this.regionItems.find((item) => item.isoCode === isoCode.toLowerCase()) || this.regionItems[2];
    if (newRegion) {
      this.regionControl.setValue(newRegion, {emitEvent: false});
    }
  }

  changeRegion(isoCode?: string): void {
    if (isoCode && Object.values(AllowedLocalesEnum).includes(isoCode.toLowerCase() as AllowedLocalesEnum)) {
      this.localeService.changeLocale(isoCode.toLowerCase() as AllowedLocalesEnum);
    } else {
      this.localeService.changeLocale();
    }
  }

  openCookieSettings() {
    (window as any).cookiehub.openSettings();
  }

  openContactUs() {
    this._modal.open(
      import('../../../modals-v2/contact-us-modal/contact-us-modal.component').then(c => c.ContactUsModalComponent),
      {
         hostClass: 'medium mobile-fs',
      },
    );
  }

  openContactSupport() {
    this._modal.open(
      import('../../../modals-v2/contact-support-modal/contact-support-modal.component').then(c => c.ContactSupportModalComponent),
      {
        hostClass: 'medium mobile-fs',
      },
    );
  }
}